import { observer } from "mobx-react-lite";
import React, { ReactNode } from "react";
import { Route, RouteComponentProps, withRouter } from "react-router";
import { Link, LinkProps } from "react-router-dom";
import { createStyles, makeStyles, Theme } from "@material-ui/core";
import { darken } from "@material-ui/core/styles";
import capitalize from "lodash/capitalize";
import startCase from "lodash/startCase";
import {
  Breadcrumbs as BreadCrumbsComponent,
  BreadcrumbsItem
} from "react-breadcrumbs-dynamic";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    breadCrumbs: {
      position: "sticky",
      color: theme.palette.getContrastText(theme.palette.background.default),
      fontStyle: "italic",
      textDecoration: "none"
    }
  })
);

const BreadCrumbChild = (routeProps: RouteComponentProps<any>) => (
  <>
    {" "}
    /{" "}
    <StyledLink to={routeProps.match.url}>
      {capitalize(startCase(routeProps.match.params.part))}
    </StyledLink>
    <Route render={BreadCrumbChild} path={routeProps.match.url + "/:part"} />
  </>
);

const AutoBreadCrumbsComponent = observer(() => {
  const classes = useStyles();
  return (
    <div className={classes.breadCrumbs}>
      <Route render={BreadCrumbChild} path="/:part" />
    </div>
  );
});

const useLinkStyles = makeStyles((theme: Theme) => ({
  root: {
    textDecoration: "none",
    color: theme.palette.getContrastText(theme.palette.background.default),
    "&:hover": {
      color: theme.palette.getContrastText(theme.palette.background.default)
    },
    "&:visited": {
      color: darken(
        theme.palette.getContrastText(theme.palette.background.default),
        0.1
      )
    },
    "&:active": {
      color: theme.palette.getContrastText(theme.palette.background.default)
    }
  }
}));

export const StyledLink = (props: LinkProps) => {
  const classes = useLinkStyles();

  return <Link className={classes.root} {...props} />;
};

/**
 *
 * AutoBreadCrumbs component is by default used in Layouts if breadCrumbs prop is not provided.
 * It uses router for bread crumbs auto generation.
 * It does not require further configuration
 *
 */
export const AutoBreadCrumbs = withRouter(AutoBreadCrumbsComponent);

/**
 *
 * BreadCrumbs component is root for configurable bread crumbs.
 * If provided application will not use router generated AutoBreadCrumbs component.
 * Bread crumbs are built based on react-through dependency using React DOM.
 * Given above, BreadCrumbs requires usage of BreadCrumbsItem
 * or withBreadCrumbs HOC (for shorter implementation) per every child view component.
 *
 */

export const BreadCrumbs = () => (
  <BreadCrumbsComponent
    separator={<span> / </span>}
    item={(props: BreadCrumbsConfig) => <BreadCrumbItemComponent {...props} />}
    finalItem={"b"}
    finalProps={{
      style: { fontWeight: "bold" }
    }}
  />
);

interface BreadCrumbsConfig {
  children: any;
  to: string;
  disableLink?: boolean;
}

export const BreadCrumbItemComponent = (config: BreadCrumbsConfig) => {
  const { children, to, disableLink } = config;

  return (
    <>
      {disableLink ? { children } : <StyledLink to={to}>{children}</StyledLink>}
    </>
  );
};

export const withBreadCrumbs = (config: BreadCrumbsConfig, view: ReactNode) => {
  return (
    <>
      <BreadcrumbsItem {...config}>{config.children}</BreadcrumbsItem>
      {view}
    </>
  );
};
